import { useCoreApiClient } from "@api/use-core-api-client";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { GroupDetailsInfoBar } from "@components/group-details/group-details-info-bar";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ProjectHeaders } from "@components/table/projects/projects-table-utils";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useAppParams } from "@router/router-helper";
import {
  fetchingProjectsFlagsSelector,
  hasFetchedFirstPageSelector,
  nextPageCursorSelector,
  groupProjectsByArchivingStateSelector,
  listTypeSelector,
} from "@store/projects/projects-selector";
import { fetchProjects } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import ProjectsDuoColorIcon from "@assets/icons/projects-duo-color.svg?react";
import { NotFoundPage } from "@pages/not-found-page";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { useCallback, useEffect, useState } from "react";
import { ButtonEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { CompanyId, GroupId } from "@custom-types/base-types";

interface Props extends Partial<BaseGroupDetailsProps> {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;

  /** The state of projects to display (active or archived) */
  projectState: ProjectArchivingState;
}

/**
 * Contains the Projects tab of the group details - can display both active and archived projects
 */
export function GroupProjects({
  group,
  isLoading = false,
  projectState,
}: Props): JSX.Element {
  const { companyId } = useAppParams();
  const coreApiClient = useCoreApiClient();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const isActiveState = projectState === ProjectArchivingState.active;
  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);
  const [sortBy, setSortBy] =
    useState<SphereDashboardAPITypes.EProjectsSortBy>();
  const [isFetchingMoreProjects, setIsFetchingMoreProjects] =
    useState<boolean>(false);

  // Get the correct list type for group projects with the current state
  const listType = useAppSelector(listTypeSelector(projectState, group?.id));
  const nextPageCursor = useAppSelector(nextPageCursorSelector(listType));
  const hasFetchedFirstPage = useAppSelector(
    hasFetchedFirstPageSelector(listType)
  );
  const projects = useAppSelector(
    groupProjectsByArchivingStateSelector(group?.id, projectState)
  );

  // Fetches the first page of group projects based on the state
  const fetchFirstPage = useCallback(
    async (companyId: CompanyId, groupId: GroupId): Promise<void> => {
      // If the first page has been fetched for this specific list type, do nothing
      if (hasFetchedFirstPage) {
        return;
      }
      try {
        await dispatch(
          fetchProjects({
            coreApiClient,
            companyId,
            groupId,
            projectArchivingState: projectState,
            sortBy,
          })
        ).unwrap();
      } catch {
        // The error slice will take care of logging and displaying the error to the user
      }
    },
    [hasFetchedFirstPage, dispatch, coreApiClient, projectState, sortBy]
  );

  /**
   * Calls the backend to fetch more projects and increases the number of projects shown on the screen.
   */
  const loadMoreProjects = useCallback(async (): Promise<void> => {
    if (companyId && nextPageCursor && group?.id) {
      trackEvent({
        name: ButtonEvents.loadMore,
        props: {
          data: `${projectState} projects`,
          source: "Group projects",
        },
      });
      setIsFetchingMoreProjects(true);

      await dispatch(
        fetchProjects({
          coreApiClient,
          companyId,
          groupId: group.id,
          projectArchivingState: projectState,
          next: nextPageCursor,
          sortBy,
        })
      );
      setIsFetchingMoreProjects(false);
    }
  }, [
    companyId,
    coreApiClient,
    dispatch,
    group?.id,
    nextPageCursor,
    projectState,
    sortBy,
    trackEvent,
  ]);

  // Fetches the first page of group projects
  useEffect(() => {
    if (companyId && group?.id) {
      void fetchFirstPage(companyId, group.id);
    }
  }, [companyId, fetchFirstPage, group?.id]);

  if (!companyId) {
    return <NotFoundPage />;
  }

  let emptyPageContent = null;
  if (projects.length === 0 && !isFetchingProjects && !isLoading) {
    emptyPageContent = (
      <EmptyPage
        title={`This group has no ${projectState} projects`}
        subtitle={
          isActiveState
            ? "You don't have any active projects connected to this group. Create a new project or add an existing active project to this group."
            : "There are no archived projects connected to this group."
        }
        icon={ProjectsDuoColorIcon}
      />
    );
  }

  return (
    <>
      <GroupDetailsInfoBar group={group} />
      {emptyPageContent ?? (
        <ProjectsTable
          companyId={companyId}
          projects={projects}
          isLoading={isFetchingProjects || isLoading || isFetchingMoreProjects}
          groupId={group?.id}
          projectArchivingState={projectState}
          loadMoreProjects={loadMoreProjects}
          sortBy={sortBy}
          setSortBy={setSortBy}
          requiredColumns={[
            ProjectHeaders.name,
            ProjectHeaders.managerAvatar,
            ProjectHeaders.projectManager,
            ProjectHeaders.lastUpdated,
            ProjectHeaders.accessLevel,
            ProjectHeaders.options,
          ]}
        />
      )}
    </>
  );
}
